import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { isSlugValid, slugify } from "@parable/slug";
import { captureTelemetryEvent } from "@/app/telemetry/posthog";
import { ApiError } from "@/data/api";
import { useSession, type SessionUser } from "@/data/auth";
import { useUpdateMyProfileMutation } from "@/data/profile";
import {
  checkSlugAvailable,
  useCreateWorkspaceMutation,
  useCurrentWorkspace,
} from "@/data/workspaces";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";

interface PropsIface {
  /** Workspace the route guard redirected from (`?workspace=` search param). */
  targetWorkspaceSlug: string | null;
  onDone: (workspaceSlug: string) => void;
  onCancel: () => void;
}

// Onboarding wizard. Workspace routes are gated on a real display name; this
// screen is the destination for that, and for creating a workspace. Steps
// self-skip, so a named user landing here just sees the create-workspace form.
// (The ambient-capture voice-enrollment step has been removed.)
export function OnboardingScreen({
  targetWorkspaceSlug,
  onDone,
  onCancel,
}: PropsIface) {
  const session = useSession();
  const { loading, memberships } = useCurrentWorkspace(null);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);
  const [nameDone, setNameDone] = useState(false);
  const completedStepsRef = useRef(0);
  const doneRef = useRef(false);

  const user = session.user;
  const explicitSlug = createdSlug ?? targetWorkspaceSlug;
  const targetMembership = explicitSlug
    ? (memberships.find((m) => m.workspace.slug === explicitSlug) ?? null)
    : (memberships[0] ?? null);

  const needsName = !nameDone && user != null && !user.hasRealDisplayName;
  const mustCreateWorkspace = memberships.length === 0 && !createdSlug;
  // Nothing unmet, no just-created workspace, and no explicit gate target →
  // creating an additional workspace (AppShell workspace switcher). An
  // explicit `?workspace=` never lands here; when its requirements are met the
  // wizard is simply done.
  const createAnother =
    !needsName &&
    !mustCreateWorkspace &&
    createdSlug == null &&
    targetWorkspaceSlug == null &&
    targetMembership != null;

  const step: "loading" | "name" | "workspace" | "done" =
    session.status === "loading" ||
    loading ||
    // A just-created workspace's membership hasn't landed in the cache yet.
    (createdSlug != null && targetMembership == null)
      ? "loading"
      : needsName
        ? "name"
        : mustCreateWorkspace || createAnother
          ? "workspace"
          : "done";

  const completeStep = useCallback(
    (name: string, extra?: Record<string, unknown>) => {
      completedStepsRef.current += 1;
      captureTelemetryEvent("onboarding_step_completed", {
        step: name,
        ...extra,
      });
    },
    [],
  );

  // A gate bounce keeps this route mounted and only changes the search
  // params — re-arm the done navigation so the wizard retries instead of
  // spinning forever.
  useEffect(() => {
    doneRef.current = false;
  }, [targetWorkspaceSlug]);

  useEffect(() => {
    if (step !== "done" || doneRef.current) return;
    const slug =
      targetMembership?.workspace.slug ?? memberships[0]?.workspace.slug;
    if (!slug) return;
    doneRef.current = true;
    if (completedStepsRef.current > 0) {
      captureTelemetryEvent("onboarding_completed", { workspace_slug: slug });
    }
    onDone(slug);
  }, [step, targetMembership, memberships, onDone]);

  if (step === "loading" || step === "done") {
    return (
      <div className="flex min-h-screen w-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-6 p-8">
      {step === "name" && user ? (
        <NameStep
          user={user}
          onSaved={() => {
            completeStep("name");
            setNameDone(true);
          }}
        />
      ) : null}

      {step === "workspace" ? (
        <WorkspaceCreateStep
          showCancel={memberships.length > 0}
          onCancel={onCancel}
          onCreated={(slug) => {
            completeStep("workspace", { workspace_slug: slug });
            setCreatedSlug(slug);
          }}
        />
      ) : null}

      <div className="flex items-center justify-between border-t border-border pt-4 text-[12px] text-muted-foreground">
        <span>{user?.email}</span>
        <button
          type="button"
          className="hover:text-foreground"
          onClick={() => void session.logout()}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function NameStep({
  user,
  onSaved,
}: {
  user: SessionUser;
  onSaved: () => void;
}) {
  const updateProfile = useUpdateMyProfileMutation();
  const [fullName, setFullName] = useState(initialName(user));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const trimmed = fullName.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      await updateProfile.mutateAsync({ fullName: trimmed });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <>
      <header>
        <h1 className="text-2xl font-semibold">Welcome to Parable</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your name labels your voice in transcripts, so it comes first.
        </p>
      </header>
      <Input
        autoFocus
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void save();
          }
        }}
        placeholder="Full name"
      />
      {error ? (
        <div className="rounded bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      <div className="flex justify-end">
        <Button
          type="button"
          disabled={saving || !fullName.trim()}
          onClick={() => void save()}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Continue
        </Button>
      </div>
    </>
  );
}

type Availability = "idle" | "checking" | "available" | "taken" | "invalid";

function WorkspaceCreateStep({
  showCancel,
  onCancel,
  onCreated,
}: {
  showCancel: boolean;
  onCancel: () => void;
  onCreated: (slug: string) => void;
}) {
  const createWorkspace = useCreateWorkspaceMutation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [availability, setAvailability] = useState<Availability>("idle");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  // Debounced availability check.
  useEffect(() => {
    if (!slug) {
      setAvailability("idle");
      return;
    }
    if (!isSlugValid(slug)) {
      setAvailability("invalid");
      return;
    }
    setAvailability("checking");
    const handle = window.setTimeout(async () => {
      try {
        await checkSlugAvailable(slug);
        setAvailability("available");
      } catch (error) {
        setAvailability(
          error instanceof ApiError && error.status === 409
            ? "taken"
            : "invalid",
        );
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [slug]);

  async function submit() {
    if (!name.trim() || availability !== "available") return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await createWorkspace.mutateAsync({
        name: name.trim(),
        slug,
      });
      onCreated(data.workspace.slug);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <header>
        <h1 className="text-2xl font-semibold">Create your workspace</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          You can rename later — the slug stays in your URLs.
        </p>
      </header>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium">Workspace name</span>
        <input
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Co"
          className="rounded border border-border bg-background px-3 py-2"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium">Slug</span>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">app.parable.so/</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value.toLowerCase());
            }}
            placeholder="acme-co"
            className="flex-1 rounded border border-border bg-background px-3 py-2"
          />
        </div>
        <span
          className="text-xs"
          style={{
            color:
              availability === "available"
                ? "var(--color-pill-ambient, #22c55e)"
                : availability === "checking"
                  ? "var(--muted-foreground, #64748b)"
                  : availability === "idle"
                    ? "transparent"
                    : "#ef4444",
          }}
        >
          {availability === "checking" && "Checking availability…"}
          {availability === "available" && "Available"}
          {availability === "taken" && "That slug is already taken"}
          {availability === "invalid" &&
            "Slug must be 3–32 lowercase chars, start with a letter, and not a reserved word"}
          {availability === "idle" && " "}
        </span>
      </label>

      {error && (
        <div className="rounded bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex justify-between">
        {showCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-border px-4 py-2"
          >
            Cancel
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          disabled={submitting || availability !== "available" || !name.trim()}
          onClick={() => void submit()}
          className="rounded bg-primary px-4 py-2 text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Creating…" : "Create workspace"}
        </button>
      </div>
    </>
  );
}

function initialName(user: SessionUser): string {
  if (user.hasRealDisplayName && user.displayName) return user.displayName;
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return name && name.toLowerCase() !== user.email.toLowerCase() ? name : "";
}
